// AlertsGuide — the "Alerts" section of the in-portal User Guide.
//
// Ported from the standalone m365-signal-alerts-guide.html. Two deliberate gains
// from being in-app: severity/family chips render through the real `Badge`
// component (so they are identical to the ones on the Alerts page), and every
// reference to a portal page is a working <Link>.
import React from 'react';
import { Link } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import Badge from '../ui/Badge';
import GuideLayout from './GuideLayout';
import {
  Section, H4, P, UL, OL, Callout, GuideDetails, GuideCard, GuideTable,
  K, B, Xref, BellSwatch,
} from './guidePrimitives';

// Same mapping the Investigations Avoided dashboard uses, so the family chips
// here match the ones an analyst sees on that page.
const FAMILY_TONE = {
  Baseline: 'info',
  Allowlist: 'purple',
  Network: 'info',
  Device: 'success',
  Identity: 'warning',
  Content: 'neutral',
  AiTM: 'critical',
  Other: 'neutral',
};
const Fam = ({ name }) => <Badge tone={FAMILY_TONE[name] || 'neutral'}>{name}</Badge>;
const GA = () => <Badge tone="neutral">Global Admin</Badge>;

// Swatch + colour name. The name matters: it is how analysts describe these to
// each other ("the violet one"), and a swatch alone isn't speakable.
const BELL_COLOURS = {
  emerald: 'text-emerald-400',
  sky: 'text-sky-400',
  indigo: 'text-indigo-400',
  rose: 'text-rose-400',
  orange: 'text-orange-400',
  violet: 'text-violet-400',
  teal: 'text-teal-400',
};
const Bell = ({ colour }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
    <BellSwatch className={BELL_COLOURS[colour]} />
    <span className="text-gray-400">{colour}</span>
  </span>
);

export default function AlertsGuide() {
  return (
    <GuideLayout
      title="Alerts"
      icon={BellIcon}
      subtitle="Reading the list, working the detail panel, and managing suppressions."
    >
      <P>
        The detection pipeline runs on a recurring cycle and raises an alert whenever a rule
        matches something suspicious. This guide covers what to do with those alerts — from
        scanning the list, through investigating one in depth, to stopping the ones that turn
        out to be noise.
      </P>

      {/* ── MULTI-TENANCY ───────────────────────────────────────────────── */}
      <Section id="multi-tenancy" title="Multi-Tenancy & User Roles">
        <P>
          Blue365Signal is built for <B>Managed Service Providers</B>. Each MSP manages one or more
          client Microsoft 365 tenants, and the portal scopes data accordingly.
        </P>

        <GuideTable
          cols={['Role', 'Sees', 'Can do']}
          rows={[
            [
              <B>Global Admin</B>,
              'All MSPs and all tenants',
              'Everything — detection rule editing, tenant onboarding, and the three global-only config pages (Teams-Phone Models, Defender File-Hash Allowlist, Defender Pass-through IP Allowlist)',
            ],
            [
              <B>MSP Admin</B>,
              "Only their MSP's tenants",
              'Alert triage, per-tenant configuration (allowlists, suppressions), PSA / ticketing setup for their MSP',
            ],
            [<B>MSP User</B>, "Only their MSP's tenants", 'Alert triage, limited configuration access'],
          ]}
        />

        <P>
          When you log in, the <B>Tenant</B> dropdown in the alerts list only shows tenants
          assigned to your MSP. Global Admins see an "All tenants" option and can switch between
          MSPs.
        </P>

        <Callout tone="info">
          <p>Two access details worth knowing:</p>
          <UL>
            <li>
              <Xref to="investigations-avoided">Investigations Avoided</Xref> is open to{' '}
              <B>any MSP member</B> — readers as well as admins — scoped to their own MSP's data.
              It is not a Global-Admin-only page.
            </li>
            <li>
              <Xref to="alert-rules">Alert Rules</Xref> editing is <B>Global Admin only</B>. Pages
              marked <GA /> in this guide are hidden entirely for everyone else.
            </li>
          </UL>
        </Callout>
      </Section>

      {/* ── DETECTION PIPELINE ──────────────────────────────────────────── */}
      <Section id="detection-pipeline" title="Detection Pipeline">
        <P>
          The detection engine evaluates incoming Microsoft 365 log data against the active rule
          library. When a rule matches, an alert is created — unless a{' '}
          <Xref to="suppression">suppression</Xref> applies, in which case the match is silently
          recorded for the <Xref to="investigations-avoided">Investigations Avoided</Xref> report.
        </P>
        <P>
          Rules come in several types. The type is shown in the <B>Type</B> column on the{' '}
          <Xref to="alert-rules">Alert Rules</Xref> page and determines how much of the rule is
          editable in the portal:
        </P>

        <GuideTable
          cols={['Type', 'What it does', 'Portal-editable?']}
          rows={[
            [
              <B>Python Fn</B>,
              'Custom logic that can inspect event fields, query baselines, check allowlists, and correlate across data sources. The bulk of the rule library.',
              'Settings and scope only — the detection logic itself lives in the pipeline codebase',
            ],
            [
              <B>Config Fn</B>,
              'A Python function driven entirely by a JSON parameter block, so one function backs many rule variants.',
              'Yes — cloneable and editable, including the parameters JSON',
            ],
            [
              <B>Log Type</B>,
              'Pattern matching on a specific log source with optional field/value conditions (e.g. Defender pass-through alerts).',
              'Yes',
            ],
            [
              <B>IP Flag</B>,
              'Matches sign-ins carrying a given IP threat flag (VPN, Tor, proxy, and so on).',
              'Yes',
            ],
            [
              <B>Baseline Deviation</B>,
              "Flags sign-ins where a field value (country, sign-in IP) falls outside the user's established baseline.",
              'Yes',
            ],
            [
              <B>Operation Baseline Deviation</B>,
              'Same idea, but filtered to a specific operation on any log type rather than to sign-ins.',
              'Yes',
            ],
          ]}
        />

        <P>
          Alerts don't only land in the portal. Depending on how the MSP and tenant are
          configured, the same alert can also raise a <B>PSA ticket</B>, appear in a{' '}
          <B>per-MSP email digest</B>, post to <B>Teams</B>, or fire an <B>outbound webhook</B>.
          Tickets and emails link back to the alert here — see{' '}
          <Xref to="deep-link">Arriving from a ticket or email</Xref>.
        </P>
      </Section>

      {/* ── ALERTS LIST ─────────────────────────────────────────────────── */}
      <Section id="alerts-list" title="Alerts List View">
        <P>
          The <Link to="/alerts" className="text-blue-400 hover:text-blue-300 hover:underline">Alerts</Link>{' '}
          page is your starting point: a filterable, paginated table of every alert the detection
          pipeline has raised.
        </P>

        <Section id="filtering" title="Filtering & Search" level={3}>
          <GuideTable
            cols={['Control', 'Description']}
            rows={[
              [<B>Tenant dropdown</B>, 'Filter by a specific tenant or "All tenants". Only shows tenants visible to your role.'],
              [
                <B>Summary counters</B>,
                <>
                  Read-only count cards: <em>Total</em>, <em>New</em>, <em>Acknowledged</em>,{' '}
                  <em>Critical</em>, <em>High</em>, <em>Medium</em> — plus <em>Test</em> when "Show
                  Test Alerts" is on. They reflect what the current filters returned; they are{' '}
                  <B>not</B> clickable. Use the column filter dropdowns to narrow the list.
                </>,
              ],
              [<B>Search box</B>, 'Free-text search across user, IP, rule name, country, and tenant. Start typing to filter instantly.'],
              [
                <B>Date range</B>,
                <>
                  <em>From</em> / <em>To</em> pickers, plus quick buttons <B>24h</B>, <B>3d</B>,{' '}
                  <B>7d</B>, <B>30d</B>, <B>All</B>. Defaults to the <B>last 1 day</B> — except on
                  Mondays, where it opens on <B>3 days</B> so the weekend's alerts are in view.
                </>,
              ],
              [<B>Show closed</B>, 'Checkbox — include alerts with Closed status (hidden by default).'],
              [<B>Show Test Alerts</B>, 'Checkbox — include alerts generated by TEST-mode rules.'],
              [
                <B>Timezone</B>,
                <>Switch displayed times: <em>Local</em>, <em>UTC</em>, <em>Eastern</em>, <em>Central</em>, <em>Mountain</em>, <em>Arizona</em>, <em>Pacific</em>, <em>Hawaii</em>.</>,
              ],
            ]}
          />
        </Section>

        <Section id="columns" title="Columns & Sorting" level={3}>
          <GuideTable
            cols={['Column', 'Notes']}
            rows={[
              ['▸ (expand chevron)', <>Expands the <Xref to="alert-detail">Alert Detail Panel</Xref> inline. Clicking anywhere on the row does the same thing.</>],
              [<K>{'</>'}</K>, "Opens the alert's stored record — every field the pipeline saved — in a modal."],
              [<B>Alert Time</B>, 'When the pipeline created the alert. Sortable (click the header).'],
              [<B>Event Time</B>, 'When the underlying event occurred in M365. Sortable — and the default sort (newest first).'],
              [<B>Severity</B>, 'Column filter dropdown: All / Critical / High / Medium / Low / Informational.'],
              [
                <B>Rule</B>,
                <>
                  Rule name, with the event's <B>log type</B> in smaller text beneath it. Column
                  filter dropdown lists all rule names. A red <B>PWD COMPROMISED</B> badge appears
                  here when the correlated sign-in confirms the password was valid.
                </>,
              ],
              [
                <B>User</B>,
                <>
                  The acting user (UPN), with the <B>tenant name</B> beneath it when "All tenants"
                  is selected. Column filter dropdown. Clipboard icon to copy the UPN.
                </>,
              ],
              [<B>IP / Country</B>, 'Source IP and country code. Clipboard icon to copy the IP.'],
              [<B>Status</B>, <>A clickable badge — see <Xref to="status-workflow">Status Workflow</Xref>. Column filter: All / New / Acknowledged / Closed / Test.</>],
              [
                <B>Actions</B>,
                <>
                  Suppression bells only, when applicable — see{' '}
                  <Xref to="inline-suppress">Inline Suppress Actions</Xref>. Status changes live on
                  the Status badge; Session Trace lives inside the expanded row.
                </>,
              ],
            ]}
          />

          <P>
            <B>Pagination</B> sits at the bottom of the table: 10 / 20 / 50 / 100 rows per page
            (default 10), with first / prev / next / last buttons.
          </P>

          <Callout tone="tip">
            <p>
              Two things that save time: use the <B>clipboard icons</B> next to user emails and IP
              addresses to copy values for other tools, and <B>drag the divider</B> between any two
              column headers to resize columns — the widths stick.
            </p>
          </Callout>
        </Section>

        <Section id="deep-link" title="Arriving from a Ticket or Email" level={3}>
          <P>
            PSA tickets and alert emails contain a <B>"View in portal"</B> link that opens a single
            alert directly, already expanded. When you arrive this way the page loads only that one
            alert — which is fast, and works even for older or already-closed alerts — and shows a
            banner with a <B>"Load all alerts for this tenant (last 30 days)"</B> button if you want
            the surrounding context.
          </P>
          <P>
            If the linked alert can't be found, the page falls back to the normal list with a note.
            That usually means the alert was reprocessed, or the ticket's alert reference doesn't
            match a stored alert. It is not an access problem.
          </P>
        </Section>
      </Section>

      {/* ── ALERT DETAIL ────────────────────────────────────────────────── */}
      <Section id="alert-detail" title="Alert Detail Panel">
        <P>
          Click the <B>▸</B> chevron on any alert row to open the detail panel. It is built from a
          stack of sections — some appear on nearly every alert, others only on specific rule types.
        </P>

        <Section id="common-sections" title="Common Sections" level={3}>
          <P>
            Sections render top to bottom in roughly the order below — highest-signal findings
            first, raw data last. A section only appears when the alert actually carries the data
            behind it, so no single alert shows all of them.
          </P>

          <GuideDetails summary={'Sign-in Outcome / "Password Compromised" banner'}>
            <p>
              Appears at the very top of Microsoft-security pass-through alerts (Defender, Entra ID
              Protection). Many of those alerts tell you an attack was attempted but not whether it{' '}
              <em>worked</em> — this banner answers that by looking up the correlated sign-in.
            </p>
            <UL>
              <li>
                <B>PASSWORD COMPROMISED — credential confirmed valid</B> (red) — the sign-in shows
                the password was accepted. Treat the credential as burned and reset it, even if the
                sign-in was ultimately blocked.
              </li>
              <li><B>SIGN-IN SUCCEEDED</B> (red) — the attacker got in.</li>
              <li>
                <B>Valid password, blocked by Conditional Access</B> — the banner adds
                residual-risk chips: green where MFA blocked it (lower risk), red where only
                location or IP blocked it (higher risk — the attacker can move to a different
                network and retry).
              </li>
            </UL>
            <p>
              The correlated sign-in is often not ingested yet when the alert fires, in which case
              the banner shows as pending with a <B>"Re-check sign-in outcome"</B> button. Click it
              after a short wait and the result is saved onto the alert for everyone.
            </p>
            <p>
              Alerts in this state also carry a red <B>PWD COMPROMISED</B> badge and a red left edge
              in the alert list, so you can spot them without expanding.
            </p>
          </GuideDetails>

          <GuideDetails summary="Linked Source Audit Event">
            <p>
              Appears on Microsoft-security pass-through alerts that describe a change but not its
              target — "Mailbox Permission Change" being the common one. The Defender payload names
              only the actor; <em>what</em> actually changed lives in the underlying Exchange admin
              audit event.
            </p>
            <p>
              Click <B>"Link source audit event"</B> and the portal correlates the alert to the
              matching audit event(s) and shows the target mailbox, the trustee, and the access
              rights granted — resolving object GUIDs to real names. A single alert often covers
              several changes (four permission grants, say); one click links them all.
            </p>
            <p>
              Microsoft's audit log typically lags the alert, so a first attempt may report the
              event isn't ingested yet. Try again a little later — the result is saved onto the
              alert once found.
            </p>
          </GuideDetails>

          <GuideDetails summary="Threat Evidence">
            <p>
              A coloured banner on Microsoft Defender alerts summarising the strongest piece of
              evidence — the malicious file, the process, the URL, the device — with the Defender
              verdict. Which evidence gets promoted depends on the alert family: Malware leads with
              the file, Execution with the process, Command-and-Control with the URL or IP.
            </p>
            <p>
              When the headline isn't a file, the banner also names the initiating process's image
              file and path — usually the thing you actually want to look at next.
            </p>
          </GuideDetails>

          <GuideDetails summary="Resolution Note">
            <p>
              Appears on <B>Closed</B> alerts. Displays the resolution text, who closed it, and
              when. If the alert was closed by the PSA ticket rather than by an analyst in the
              portal, the note is tagged <B>"via PSA ticket #N"</B>.
            </p>
          </GuideDetails>

          <GuideDetails summary="PSA Ticket">
            <p>
              If a PSA ticket was opened for this alert, this section shows the <B>PSA system</B>,
              the <B>ticket number</B> (linked straight to the ticket, and copyable), and when it
              was opened.
            </p>
            <p>Ticket state moves between the two systems, but not symmetrically:</p>
            <UL>
              <li>
                <B>Blue365Signal → PSA</B> — <B>closing</B> an alert closes the ticket, provided you
                leave the <em>"Also close linked ticket"</em> checkbox ticked in the close dialog.
                Moving a closed alert <B>back to New or Acknowledged reopens the ticket</B>, so the
                two stay in step. Status changes that don't cross the closed boundary (New →
                Acknowledged) leave the ticket alone.
              </li>
              <li>
                <B>PSA → Blue365Signal</B> — closing the ticket in the PSA closes the alert here
                automatically, on a recurring schedule (not instant). This is{' '}
                <B>first-close-wins</B>: if an analyst already closed the alert with their own note,
                the PSA sync leaves that note alone.
              </li>
            </UL>
            <Callout tone="warn">
              <p>
                If the PSA ticket is moved to a different Board or Queue, ticket synchronization
                will likely <B>break</B>. Keep tickets in their original Board/Queue to maintain
                sync.
              </p>
            </Callout>
          </GuideDetails>

          <GuideDetails summary="Evaluation Note">
            <p>
              A fixed piece of analyst guidance shown on certain rules, explaining what the rule was
              built to catch and what to weigh when triaging it. It is authored per rule, not
              generated per alert, so the same note appears on every alert from that rule. Most
              rules don't carry one.
            </p>
          </GuideDetails>

          <GuideDetails summary="AiTM Click Correlation">
            <p>
              Appears on adversary-in-the-middle rules that tie a phishing URL click to a
              subsequent sign-in. Shows:
            </p>
            <UL>
              <li>
                <B>Clicked URL</B> — rendered defanged (<K>example[.]com</K>) so it can't be clicked
                by accident, with a copy button
              </li>
              <li><B>Click Time</B></li>
              <li>
                <B>Click → Sign-in Gap</B> — the interval between the click and the sign-in. A very
                short gap is the classic AiTM proxy signature.
              </li>
            </UL>
          </GuideDetails>

          <GuideDetails summary="Source Event">
            <p>
              The Microsoft 365 event data that triggered the alert, formatted per log type.
              Collapsed by default. Typically shows:
            </p>
            <UL>
              <li><B>Timestamp</B> — when the event occurred</li>
              <li><B>User / Activity</B> — the acting user and operation</li>
              <li><B>IP address</B></li>
              <li><B>Result</B> — success or failure of the action</li>
              <li><B>Application</B> — the app involved in the event</li>
            </UL>
            <p>
              The full <B>Raw Event JSON</B>, with a <B>Copy</B> button, is at the bottom of the
              panel.
            </p>
          </GuideDetails>

          <GuideDetails summary="IP Intelligence">
            <p>Enriched IP data from ProxyCheck.io plus Blue365Signal's own cross-tenant telemetry:</p>
            <UL>
              <li><B>IP</B> — linked to iplocation.net for external lookup</li>
              <li><B>Risk score</B>, <B>Country</B>, <B>Provider</B> (ISP/ASN)</li>
              <li><B>Detections</B> — proxy / VPN / threat flags</li>
              <li>
                <B>Cross-tenant usage</B> — how many other monitored tenants and users have been
                seen on this IP. A shared corporate VPN looks very different from an IP seen once,
                in one tenant, by one user.
              </li>
              <li><B>"List users"</B> button — see the note below on exactly what it shows</li>
              <li>
                <B>"Re-fetch IP enrichment"</B> button — forces a fresh ProxyCheck.io
                lookup and saves the result onto the alert
              </li>
            </UL>
            <Callout tone="info">
              <p>
                <B>"List users" respects MSP separation.</B> It lists users on that IP in{' '}
                <em>this</em> tenant, then separately users in <B>other tenants you are authorized
                to see</B> (each badged with its tenant and MSP). Identities in tenants you cannot
                read are <B>counted but withheld</B>. The cross-tenant list is capped at the top 500
                by activity.
              </p>
            </Callout>
          </GuideDetails>

          <GuideDetails summary="Baseline Comparison">
            <p>
              Shows how the event deviates from the user's established behaviour, as a deviation
              count plus a four-column table: <B>Field</B>, <B>Event value</B>, <B>7-day
              baseline</B>, <B>30-day baseline</B>. Fields compared: Country, Provider (ISP), State,
              City, whether the IP is in the baseline (expandable to the known-IP list), hour of
              day, OS, Intune managed, Intune compliant, and device in DRMM.
            </p>
            <p>
              See <Xref to="baseline">Baseline Comparison</Xref> below for how to read the verdict
              colours.
            </p>
          </GuideDetails>

          <GuideDetails summary="Correlated Sign-in & Anomaly / IDs">
            <p>
              A two-column block. On the left, <B>Correlated Sign-in</B> gives the sign-in context
              behind a non-sign-in event — <B>Device name</B> and <B>Trust Type</B> (Azure AD
              joined, Hybrid joined, Workplace/registered).
            </p>
            <p>
              On the right, <B>Anomaly / IDs</B> shows the deviation that triggered the rule (type,
              observed IP, observed country) and, beneath it, the <B>Rule suppressions</B> list
              described next.
            </p>
          </GuideDetails>

          <GuideDetails summary="Rule Suppressions (inside Anomaly / IDs)">
            <p>
              Shows which suppression settings are <B>switched on for the rule that fired</B> —
              useful context for judging how much filtering the alert already survived.
            </p>
            <UL>
              <li>
                <B>✓</B> — this suppression is enabled on the rule. Since you're looking at an
                alert, none of them matched this event.
              </li>
              <li>
                <B>↺</B> — the rule has <em>"Alert if a Known Malicious IP"</em> enabled, meaning a
                known-malicious IP forces the alert through regardless of any suppression.
              </li>
            </UL>
            <Callout tone="warn">
              <p>
                This list is <B>not exhaustive</B>. It covers the device and baseline suppressions
                only — Entra Joined, Intune Managed, Intune Compliant, DRMM Match, MSP Tenant
                Baseline, User Baseline, CTAP Inbound Trust, the IPv6 /64 modifier, and the
                malicious-IP override. The allowlist tiers — mailbox pair, user/app pair, group/app
                pair, file hash, benign IP, Teams-phone, provider-in-baseline — do <B>not</B> appear
                here. For the full picture, open the rule in the{' '}
                <Xref to="rule-editor">Rule Editor</Xref>.
              </p>
            </Callout>
          </GuideDetails>

          <GuideDetails summary="Device Posture">
            <p>
              Device context from both <B>Directory</B> (Entra ID) and <B>MDM</B> (Intune). Loads on
              demand when the alert — or the sign-in correlated to it — carries a device ID, so it
              appears on Exchange and SharePoint alerts too, not just sign-ins.
            </p>
            <p>
              Three outcomes: the device is known to both; known to the directory but{' '}
              <em>not enrolled</em> in Intune; or <em>unknown to the directory entirely</em>, which
              is shown as a red warning and is itself a risk signal.
            </p>
          </GuideDetails>

          <GuideDetails summary="Conditional Access Policies">
            <p>
              Lists the Conditional Access policies evaluated during the sign-in, with the total
              count in the heading and a <B>Show N inactive</B> / <B>Hide N inactive</B> toggle.
              Each policy carries an outcome badge:
            </p>
            <UL>
              <li><B>Applied</B> — the policy's grant controls were satisfied</li>
              <li><B>Blocked</B> — the policy blocked the sign-in</li>
              <li>
                <B>Report (Fail)</B> — report-only policy that <em>would</em> have blocked. Worth
                reading: it usually means a control you haven't enforced yet would have stopped
                this.
              </li>
              <li><B>Report (Pass)</B> — report-only policy that would have allowed it</li>
              <li><B>Not Applied</B> — conditions not met</li>
            </UL>
            <Callout tone="info">
              <p>
                Hidden by default: <em>Not Applied</em>, <em>Not Enabled</em>, and <B>both</B>{' '}
                report-only-pass outcomes. So a report-only policy that quietly passed sits behind
                the <em>Show N inactive</em> toggle — only report-only <em>failures</em> are
                surfaced up front.
              </p>
            </Callout>
          </GuideDetails>

          <GuideDetails summary="Authentication App Policy Evaluation">
            <p>
              Present on sign-ins where Microsoft evaluated Authenticator app policies. Each row
              names the policy (Number Match, App Lock, and similar), how it evaluated, its status,
              and the application involved.
            </p>
          </GuideDetails>

          <GuideDetails summary="IP Enrichment Record">
            <p>
              The complete stored enrichment record for the source IP, as JSON. Collapsible, with a{' '}
              <B>Copy</B> button. Useful when you want a field the IP Intelligence summary doesn't
              surface.
            </p>
          </GuideDetails>

          <GuideDetails summary="Raw Event JSON">
            <p>
              Always last. The complete, unprocessed event payload, collapsible with a <B>Copy</B>{' '}
              button for export to other investigation tools.
            </p>
          </GuideDetails>
        </Section>

        <Section id="rule-specific" title="Rule-Specific Sections" level={3}>
          <P>Certain alert types include additional investigation sections:</P>

          <GuideCard title="Suspicious Inbox Rule Creation">
            <p>
              Displays a <B>Rule / Operation Parameters</B> table showing exactly what the inbox
              rule was configured to do — forward to, delete, move, mark as read. Critical for
              determining whether the rule is malicious.
            </p>
          </GuideCard>

          <GuideCard title="Anomalous Mailbox Access">
            <UL>
              <li>
                <B>Aggregation Summary</B> — the detection window, mailbox owner, the delegate or
                actor who did the accessing, access count, folders, source IPs and client info.
                Unlike most alerts, these cover a window of activity rather than one event.
              </li>
              <li>
                <B>Mailboxes Accessed</B> — one card per mailbox, each listing who accessed it.
                Email subjects are <em>masked by default</em> (••••). Click{' '}
                <B>"Reveal N subjects (logged)"</B> to unmask, and <B>"Show N more…"</B> to expand
                past the first few items.
              </li>
            </UL>
            <Callout tone="warn">
              <p>
                Email subjects can contain PHI or PII, so revealing them is <B>recorded in the
                portal activity log</B> — who revealed, which alert, which mailbox, and how many
                subjects. The subject text itself is never logged. Reveal is per-mailbox and
                deliberate; that's by design, not friction to work around.
              </p>
            </Callout>
          </GuideCard>

          <GuideCard title="Rare Application Consent">
            <UL>
              <li><B>Source</B> — the user (UPN) and IP who granted consent</li>
              <li><B>Target Resource</B> — Display Name, Object ID, Type (ServicePrincipal)</li>
              <li>
                <B>Permissions Granted</B> — each permission tagged <em>New</em> / <em>Existing</em>{' '}
                / <em>Revoked</em> and <em>Application</em> (app-only, higher risk) vs{' '}
                <em>Delegated</em>, with the API and scope (e.g.{' '}
                <K>Microsoft Graph: openid, email, profile, User.Read</K>)
              </li>
            </UL>
          </GuideCard>

          <GuideCard title="Directory Change Alerts (CA policy edits, role assignments, group changes)">
            <UL>
              <li>
                <B>Source</B> — who initiated the change (UPN and IP), or the application if it was
                app-driven
              </li>
              <li>
                <B>Target Resource</B> — what was changed, with <B>Modified Properties</B> rendered
                as a colour-coded diff: <span className="text-emerald-400">Added</span> /{' '}
                <span className="text-red-400">Removed</span> /{' '}
                <span className="text-amber-400">Changed</span>, and Created / Deleted for whole
                objects
              </li>
            </UL>
            <p>
              Conditional Access policies reference groups, users and roles by bare GUID. The portal
              resolves those to friendly names automatically from the nightly directory snapshot. If
              an object was created the same day it was referenced it won't be in that snapshot yet
              — a <B>"Resolve Name"</B> button appears next to the GUID, which searches the recent
              directory audit feed and saves the name onto the alert once found.
            </p>
          </GuideCard>

          <GuideCard title="Microsoft Security Pass-through Alerts">
            <p>
              Alerts relayed from Microsoft's own detections — Defender for Endpoint, Identity,
              Office 365, Cloud Apps, App Governance, Microsoft 365 Defender (XDR) and Entra ID
              Protection. These carry:
            </p>
            <UL>
              <li>
                <B>Threat Evidence</B> — the headline evidence and Defender verdict; may include
                file hashes, processes, URLs, IPs, accounts or device details depending on the
                product
              </li>
              <li>
                Often a <B>Sign-in Outcome banner</B> and, where relevant, a{' '}
                <B>Linked Source Audit Event</B> — both described under Common Sections above
              </li>
            </UL>
            <p>
              These are also where the alert-title, alert-description and alert-actor{' '}
              <Xref to="inline-suppress">quick-suppress</Xref> actions apply.
            </p>
          </GuideCard>
        </Section>
      </Section>

      {/* ── STATUS WORKFLOW ─────────────────────────────────────────────── */}
      <Section id="status-workflow" title="Status Workflow">
        <P>Every alert moves through a three-stage lifecycle:</P>

        <div className="flex items-center gap-2 flex-wrap my-4">
          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600/20 text-blue-300 border border-blue-500/40">New</span>
          <span className="text-gray-600">→</span>
          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 text-gray-300 border border-gray-700">Acknowledged</span>
          <span className="text-gray-600">→</span>
          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 text-gray-300 border border-gray-700">Closed</span>
        </div>

        <UL>
          <li><B>New</B> — freshly generated by the pipeline. Requires analyst attention.</li>
          <li><B>Acknowledged</B> — an analyst has seen the alert and is working on it.</li>
          <li><B>Closed</B> — investigation complete. A <B>Resolution Note</B> is required.</li>
        </UL>

        <P>
          Status is changed from the <B>Status badge</B> itself — click it and pick New,
          Acknowledged or Closed. There is no status control in the Actions column.
        </P>

        <Section id="closing" title="Closing an Alert" level={3}>
          <P>
            Choosing <B>Closed</B> opens a dialog. A resolution note is mandatory — the confirm
            button stays disabled until you type one. Three preset chips (<em>False positive</em>,{' '}
            <em>Benign true positive</em>, <em>Remediated</em>) prefill a prefix you can expand on.
          </P>
          <P>If a PSA ticket is linked to the alert, the dialog also offers:</P>
          <UL>
            <li>
              <B>"Also close linked ticket #N"</B> — ticked by default. Posts your resolution note
              to the ticket, then sets it complete. Untick it to close the alert without touching
              the ticket.
            </li>
            <li>
              <B>"Log time on the ticket as &lt;your PSA user&gt;"</B> — optional, and only offered
              when your portal account is mapped to a PSA user for that MSP. Enter whole minutes;
              your resolution note becomes the time-entry summary.
            </li>
          </UL>
          <P>
            If no ticket is linked, the dialog explains why — either PSA routing was enabled for the
            tenant after this alert was generated, or ticket creation is disabled for that tenant.
          </P>
        </Section>

        <Section id="reopening" title="Reopening an Alert" level={3}>
          <P>
            Closing an alert is reversible. Setting it back to <B>New</B> or <B>Acknowledged</B>{' '}
            clears the resolution note, the closed-by and the closed-at, and <B>reopens the linked
            PSA ticket</B> — so you don't end up working an open alert against a completed ticket.
          </P>
          <P>
            One thing does not come back: a <B>logged time entry stays on the ticket</B>. That's
            intentional — time worked is time worked, and reversing it would misstate the record. If
            the time was logged in error, correct it in the PSA.
          </P>
        </Section>
      </Section>

      {/* ── INVESTIGATION WORKFLOW ──────────────────────────────────────── */}
      <Section id="investigation" title="Alert Investigation Workflow">
        <P>A recommended sequence for investigating an alert:</P>
        <OL>
          <li><B>Expand the alert</B> — click the ▸ chevron, or anywhere on the row.</li>
          <li>
            <B>Check the top banners first</B> — a <em>Sign-in Outcome</em> banner answers "did it
            actually work?" before anything else. If it reads <em>pending</em>, click{' '}
            <B>Re-check sign-in outcome</B>.
          </li>
          <li><B>Read the Evaluation Note</B> — if present, it gives analyst guidance for this rule.</li>
          <li>
            <B>Review Source Event</B> — who, when, from where, what app. On a pass-through alert
            that names an actor but not a target, click <B>Link source audit event</B>.
          </li>
          <li><B>Check IP Intelligence</B> — source IP risk, proxy/VPN flags, cross-tenant usage.</li>
          <li><B>Review Baseline</B> — how many deviations from normal? Is this a known pattern?</li>
          <li><B>Check Device Posture</B> — is the device managed, compliant, known to the directory at all?</li>
          <li>
            <B>Assess CA Policies</B> — what was enforced, what blocked, and what a report-only
            policy <em>would</em> have blocked.
          </li>
          <li><B>Run Session Trace</B> — what else happened in the same session, if a session ID exists.</li>
          <li><B>Check Token Use</B> — what the token was used for after authentication.</li>
          <li><B>Set status</B> — Acknowledge → investigate → Close with a Resolution Note.</li>
        </OL>

        <Callout tone="info">
          <p>
            Session Trace is an <B>admin action</B>. If you run it and get no results, check your
            role before assuming there's no data. Token Use and on-demand IP re-enrichment are
            available to every portal user.
          </p>
        </Callout>

        <Section id="session-trace" title="Session Trace" level={3}>
          <P>
            Reconstructs everything that happened under the same sign-in session, from the event
            timestamp forward 24 hours — the section heading states the window explicitly. Click{' '}
            <B>"Run Trace"</B>, or <B>"Re-run"</B> to refresh. Two tabs:
          </P>

          <H4>Timeline tab</H4>
          <GuideTable
            cols={['Column', 'Description']}
            rows={[
              ['Time', 'When each action occurred'],
              ['Log type', 'Source: sign-in, Exchange, SharePoint, etc.'],
              [
                'Operation',
                'What was done (e.g. MailItemsAccessed, New-InboxRule). Sensitive operations — folder permission changes, inbox rules, role assignments, consent grants — are highlighted in amber.',
              ],
              ['Client IP', 'IP at the time of each action'],
              ['Provider', 'ISP/ASN for the IP'],
              [
                'Source',
                <>
                  How the row was matched: <B>session</B> = a direct session-ID hit;{' '}
                  <B>user-window</B> = same user in the same time window, for log types that don't
                  record a session ID. Treat <em>user-window</em> rows as strong circumstantial
                  evidence rather than proof they belong to the session.
                </>,
              ],
            ]}
          />
          <P>Each row expands to show its own Raw Event JSON.</P>

          <H4>Summary tab</H4>
          <P>
            Sensitive-operation cards, a frequency list of the top operations, a per-IP table
            (provider, network type, country, risk), and the set of providers touched during the
            session.
          </P>

          <Callout tone="tip">
            <p>
              Expect a Session Trace to take roughly 5–15 seconds. The button is disabled — with a{' '}
              <em>"No session ID on event"</em> note — when the alert's event doesn't carry a
              session ID, which is common for non-sign-in log types.
            </p>
          </Callout>
        </Section>

        <Section id="token-use" title="Token Use Analysis" level={3}>
          <P>
            Click <B>"Load"</B> to see how the authentication token was used <em>after</em> the
            sign-in. This reads the <B>non-interactive</B> sign-in feed — the one where
            refresh-token reuse and AiTM token replay actually show up. Interactive sign-in logs
            never record it. The window runs from 2 hours before the alert event through 24 hours
            after.
          </P>
          <UL>
            <li>
              <B>Timeline tab</B> — chronological table: Time, App, Token type, Client IP, ASN,
              Country, Result. A <em>successful</em> token use from an ASN or country the user has
              never used is highlighted — that's the replay signature.
            </li>
            <li>
              <B>Summary tab</B> — the originating interactive sign-in legs the tokens were stitched
              back to, plus every IP and ASN touched.
            </li>
          </UL>
          <Callout tone="info">
            <p>
              Proxy-style AiTM — where the attacker relays a lookalike login host — does <B>not</B>{' '}
              appear in this feed. It bypasses Entra sign-in logging entirely. An empty Token Use
              panel is not evidence that no token was replayed.
            </p>
          </Callout>
        </Section>

        <Section id="ip-intel" title="IP Intelligence" level={3}>
          <P>
            IP enrichment is sourced from <B>ProxyCheck.io</B> and cross-referenced with
            Blue365Signal's own telemetry. Key fields:
          </P>
          <UL>
            <li><B>Risk score</B> — numeric risk assessment from ProxyCheck.io</li>
            <li><B>Proxy / VPN flags</B> — whether the IP is a known proxy, VPN endpoint, or Tor exit node</li>
            <li>
              <B>Cross-tenant usage</B> — how many monitored tenants and users have been seen on
              this IP. Many users in the same tenant usually means a shared corporate VPN or office
              egress; a single user in a single tenant is an isolated IP and more interesting.
            </li>
            <li>
              <B>"List users"</B> — users on this IP in the current tenant, plus users in{' '}
              <B>other tenants you are authorized to see</B>, each badged with tenant and MSP.
              Identities in tenants outside your access are counted but withheld; the cross-tenant
              list caps at the top 500 by activity.
            </li>
            <li>
              <B>"Re-fetch IP enrichment"</B> — forces a fresh ProxyCheck.io lookup and
              saves it onto the alert, so refreshed values survive a reload. Available to every
              portal user; if the IP was enriched in the last 24 hours you are asked to confirm
              before it is re-fetched.
            </li>
          </UL>
        </Section>

        <Section id="baseline" title="Baseline Comparison" level={3}>
          <P>
            Baselines are computed per user over <B>7-day</B> and <B>30-day</B> windows. The
            comparison shows how many dimensions of the current event deviate from the user's
            established patterns, with the deviation count in the section heading and the verdict
            colour on the <em>Event value</em> column — green where it matches the baseline, red
            where it deviates, grey where there's no baseline data.
          </P>
          <P>
            High deviation counts suggest unusual activity, but context matters: a travelling user
            will naturally show new country and provider deviations. "No baseline data" is not the
            same as a deviation — a new or infrequently active user simply hasn't built one yet.
          </P>
        </Section>
      </Section>

      {/* ── SUPPRESSION ─────────────────────────────────────────────────── */}
      <Section id="suppression" title="Suppression Overview">
        <P>
          Suppression is the <B>core value proposition</B> of Blue365Signal. Every detection match is
          evaluated against a layered set of suppression checks — baselines, allowlists, device
          posture, network trust and content analysis. When a match is suppressed, no alert is
          created, and an analyst never spends time on it.
        </P>
        <P>
          Over a typical week the system suppresses <B>thousands of would-be alerts</B>, each one an
          investigation that was automatically avoided. The{' '}
          <Xref to="investigations-avoided">Investigations Avoided</Xref> dashboard quantifies it.
        </P>

        <Section id="inline-suppress" title="Inline Suppress Actions" level={3}>
          <P>
            Some alerts display coloured <B>bell-with-a-slash</B> icons in the Actions column. These
            create a suppression directly from the alert in front of you, without hunting for the
            right configuration page. There are <B>seven</B>, each a different colour:
          </P>

          <GuideTable
            cols={['Icon', 'Action', 'Appears on']}
            rows={[
              [<Bell colour="emerald" />, 'Allowlist mailbox pair(s)', 'Mailbox-access rules'],
              [<Bell colour="sky" />, 'Allowlist this (user, application) pair', 'Rules using the user/app allowlist'],
              [<Bell colour="indigo" />, 'Allowlist a (group, application) pair', 'Rules using the group/app allowlist'],
              [<Bell colour="rose" />, <>Stop this Defender alert <B>title</B></>, 'Microsoft security pass-through alerts'],
              [<Bell colour="orange" />, <>Stop this Defender alert <B>description</B></>, 'Microsoft security pass-through alerts'],
              [<Bell colour="violet" />, <>Stop this Defender alert <B>actor</B></>, 'Microsoft security pass-through alerts'],
              [<Bell colour="teal" />, 'Add this IP to the benign-IP allowlist', 'Microsoft security pass-through alerts'],
            ]}
          />

          <Callout tone="info">
            <p>
              All seven are <B>admin actions</B>, and each only appears when the rule that fired has
              that suppression tier switched on. If you expect a bell and don't see one, the rule
              isn't wired to consume that allowlist — that's a rule setting, not a bug.
            </p>
          </Callout>

          <GuideDetails summary="Allowlist mailbox pair(s)">
            <p>
              Opens a dialog listing every (Mailbox, Actor) pair found in the alert. For each pair
              you choose how the actor should be matched:
            </p>
            <UL>
              <li><B>UPN</B> — match by the accessing user's email</li>
              <li><B>App Name</B> — match by application display name</li>
              <li><B>App ID</B> — match by application GUID</li>
              <li><B>Service SID</B> — match a service or system principal</li>
              <li><B>Any *</B> — wildcard; any actor accessing this mailbox is suppressed</li>
            </UL>
            <p>
              Includes a <B>Notes</B> field, a <B>Preview</B> of the resulting entry, and{' '}
              <B>Select all / Clear</B>. Entries land on the{' '}
              <Xref to="config-mailbox">Mailbox-Pair Allowlist</Xref> page.
            </p>
          </GuideDetails>

          <GuideDetails summary="Allowlist (user, application) pair">
            <p>
              Suppresses this rule when this user signs in through this specific application. The
              application is matched by <B>GUID</B>, not display name. User options:
            </p>
            <UL>
              <li><B>Full UPN</B> — this one user</li>
              <li>
                <B>Any guest from a domain</B> (<K>*@domain.tld</K>) — matches B2B guests from that
                domain only; it cannot suppress your own internal users
              </li>
              <li><B>Any *</B> — any user, for that one application</li>
            </UL>
            <p>
              Wildcarding both sides at once is rejected. Entries land on the{' '}
              <Xref to="config-app">User/App Pair Allowlist</Xref> page.
            </p>
          </GuideDetails>

          <GuideDetails summary="Allowlist (group, application) pair">
            <p>
              Same idea, but scoped by <B>Entra group membership</B> instead of a named user — the
              natural fit for "contractors in this CA-exception group are expected to do this". You
              pick the group; the application is prefilled from the alert.
            </p>
            <p>
              There is deliberately <B>no "any group" wildcard</B>. Entries land on the{' '}
              <Xref to="config-app">Group/App Pair Allowlist</Xref> page.
            </p>
          </GuideDetails>

          <GuideDetails summary="Stop this Defender alert title">
            <p>Suppresses future pass-through alerts whose <B>title</B> matches. Options:</p>
            <UL>
              <li><B>Exact title</B> — must match the full alert title</li>
              <li><B>Contains</B> — title contains the substring</li>
            </UL>
            <p>The dialog shows the blast radius — which rule is being modified and what that rule is scoped to.</p>
            <Callout tone="warn">
              <p>
                Title suppression is broad: it applies wherever the modified rule applies, which is
                usually <B>all tenants in that rule's scope</B>. A known-malicious IP still forces
                the alert through. Use the description or actor variants when you need something
                narrower.
              </p>
            </Callout>
          </GuideDetails>

          <GuideDetails summary="Stop this Defender alert description">
            <p>
              Matches on the alert <B>description</B> instead of the title — the right choice when
              the distinguishing text is in the body, as with a named approved remote-access tool.
              Options:
            </p>
            <UL>
              <li><B>Contains</B> (default) — description contains the substring</li>
              <li><B>Exact description</B> — must match the full description</li>
            </UL>
            <p>
              An optional <B>Detector ID</B> field narrows the match to one Microsoft detector,
              making the suppression considerably more precise than title matching.
            </p>
          </GuideDetails>

          <GuideDetails summary="Stop this Defender alert actor">
            <p>
              Matches on the <B>evidence actor</B> — the account named in the alert's evidence.
              Built for Microsoft substrate service accounts that generate non-actionable noise (the
              originating case was <K>{'NT SERVICE\\MSExchangeAdminApiNetCore'}</K> provisioning an
              eDiscovery mailbox).
            </p>
            <p>
              Options are <B>Exact</B> (default) or <B>Contains</B>. The actor is prefilled from the
              alert's evidence.
            </p>
            <Callout tone="info">
              <p>
                This is safer than it sounds for the common case: the same activity performed by a
                real user carries a <em>different</em> evidence actor, so suppressing the service
                account does not blind you to a human doing the same thing.
              </p>
            </Callout>
          </GuideDetails>

          <GuideDetails summary="Add this IP to the benign-IP allowlist">
            <p>
              For IPs Microsoft mis-flags as malicious — a legitimate SaaS platform's egress, say.
              Drops pass-through alerts whose source IP is on the list.
            </p>
            <p>
              The list is <B>global across all tenants</B> and exact-IP only (no CIDR ranges). A
              known-malicious IP still alerts. Entries land on the{' '}
              <Xref to="config-ip">Defender Pass-through IP Allowlist</Xref> page.
            </p>
          </GuideDetails>
        </Section>

        <Section id="suppress-methods" title="Suppression Method Families" level={3}>
          <P>The pipeline evaluates many suppression methods, grouped into families:</P>

          <GuideTable
            cols={['Family', 'Examples', 'Source']}
            rows={[
              [
                <Fam name="Baseline" />,
                "Country in user's sign-in baseline, user/tenant sign-in IP baseline, provider in baseline",
                'Auto-computed from sign-in history (7/30-day)',
              ],
              [
                <Fam name="Device" />,
                'DRMM-managed device, compliant device (device-id, shared IP, user+IP context), trusted/joined device',
                'Intune + DRMM + Entra device registration',
              ],
              [
                <Fam name="Allowlist" />,
                'Defender title/description/actor excluded, mailbox-pair allowlist, country code suppressed, benign-IP allowlist, file-hash allowlist',
                <>Manually configured via <Xref to="config-pages">configuration pages</Xref></>,
              ],
              [<Fam name="Network" />, 'Tenant trusted VPN / hosting provider', 'Configured per-tenant'],
              [
                <Fam name="Identity" />,
                'Cross-tenant access (CTAP) inbound trust, consent provisioning replay',
                'Tenant trust relationships and provisioning context',
              ],
              [
                <Fam name="Content" />,
                'Benign inbox rule (no destructive action), mailbox logon type owner-authorized',
                "Rule logic analyzes the operation's own parameters",
              ],
              [
                <Fam name="AiTM" />,
                'Trusted (click host, app) pair — auto-discovered or manually forced',
                <>See <Xref to="config-aitm">AiTM Trusted Domains / Pairs</Xref></>,
              ],
              [
                <Fam name="Other" />,
                'Any method without a family mapping — including the (user, app) and (group, app) pair allowlists',
                'Contextual logic within rules',
              ],
            ]}
          />

          <Callout tone="info">
            <p>
              <B>"Other" is a labelling bucket, not a mystery.</B> The (user, app) and (group, app)
              pair suppressions work exactly as configured — they simply have no family assigned in
              the reporting layer, so they roll up under <em>Other</em> on the Investigations Avoided
              breakdown. Read the <em>Method</em> column rather than the family to see what actually
              fired.
            </p>
          </Callout>

          <Callout tone="info">
            <p>
              Even when a suppression would normally clear an alert, known <B>malicious IPs</B> (from
              the <Xref to="config-ip">Known Malicious IPs</Xref> list) override suppression and
              force the alert through — provided the rule has the malicious-IP override enabled.
            </p>
          </Callout>
        </Section>
      </Section>

      {/* ── INVESTIGATIONS AVOIDED ──────────────────────────────────────── */}
      <Section id="investigations-avoided" title="Investigations Avoided">
        <P>
          The{' '}
          <Link to="/admin/investigations-avoided" className="text-blue-400 hover:text-blue-300 hover:underline">
            Investigations Avoided
          </Link>{' '}
          page quantifies the impact of the suppression system. It answers: <em>how many
          false-positive alerts did Blue365Signal automatically prevent?</em>
        </P>
        <P>
          It is open to <B>any MSP member</B> — readers as well as admins — showing their own MSP's
          data. Global Admins see everything.
        </P>

        <Section id="ia-controls" title="Dashboard Controls" level={3}>
          <GuideTable
            cols={['Control', 'Description']}
            rows={[
              [
                <B>Window presets</B>,
                <>
                  <B>7d</B> / <B>30d</B> / <B>90d</B>. Defaults to 7 days. Every card, chart and
                  table on the page reflects the selected window.
                </>,
              ],
              [<B>MSP dropdown</B>, 'Filter by MSP (Global Admins see all MSPs).'],
              [<B>Tenant dropdown</B>, '"All tenants in [MSP]" or drill into a specific tenant.'],
              [
                <B>Include TEST-mode rules</B>,
                'Checkbox, off by default. TEST-rule suppressions are validation data, not customer benefit — leave it off for reporting, turn it on when tuning a rule before promotion.',
              ],
              [<B>Timezone</B>, 'The standard portal picker: Local, UTC, Eastern, Central, Mountain, Arizona, Pacific, Hawaii.'],
              [<B>Refresh</B>, 'Reload suppression telemetry.'],
            ]}
          />
        </Section>

        <Section id="ia-cards" title="Summary Cards" level={3}>
          <P>
            Each card is labelled with the window it covers (e.g. "Investigations avoided — last 7
            days"), so it always matches the preset you selected.
          </P>
          <GuideTable
            cols={['Card', 'Meaning']}
            rows={[
              [
                <B>Investigations avoided</B>,
                'Unique investigation-worthy events that were suppressed (deduplicated). This is the number to quote — it approximates analyst hours not spent.',
              ],
              [
                <B>Suppressed matches</B>,
                'Total raw matches suppressed. Higher than investigations avoided, because one investigation can be triggered by many matches.',
              ],
              [<B>Tenants with suppressions</B>, 'How many tenants had at least one suppression in the window.'],
              [<B>Top method</B>, 'The most frequently triggered suppression method.'],
            ]}
          />
          <Callout tone="info">
            <p>
              "Sign-in from New Country" country-baseline suppressions are counted{' '}
              <B>once per user + country + day</B> (repeat sign-ins collapsed), because that one
              combination fires on every repeat sign-in and would otherwise dominate the totals. All
              other methods count each individual match.
            </p>
          </Callout>
        </Section>

        <Section id="ia-breakdown" title="Breakdown Tables" level={3}>
          <UL>
            <li><B>Suppressed Alerts Per Day</B> — daily trend chart across the selected window.</li>
            <li>
              <B>By Suppression Method</B> — Method, Family (Baseline / Allowlist / Device / Network
              / Identity / Content / AiTM / Other), and Count.
            </li>
            <li><B>By Suppressed Alert Type</B> — Rule name, Severity, Count.</li>
            <li><B>By Severity</B> — suppressed counts by severity level.</li>
            <li><B>By MSP</B> — tenants and counts per MSP.</li>
            <li>
              <B>By Customer</B> — tenant-level breakdown, sorted by count descending.{' '}
              <B>Click a tenant</B> to audit its individual suppressed events.
            </li>
          </UL>
        </Section>

        <Section id="ia-audit" title="Suppressed Events Audit" level={3}>
          <P>
            Clicking a tenant in the "By Customer" table loads a detailed <B>Suppressed Events</B>{' '}
            table at the bottom of the page, capped at 500 events:
          </P>
          <GuideTable
            cols={['Column', 'Description']}
            rows={[
              [<B>Event time</B>, 'When the original event occurred.'],
              [<B>Rule</B>, 'Which detection rule matched (column filter available).'],
              [<B>Sev</B>, 'Severity of the would-be alert (column filter available).'],
              [<B>Suppressed by</B>, 'Which suppression method cleared it (column filter available).'],
              [<B>User</B>, 'The affected user.'],
              [<B>IP</B>, 'Source IP.'],
              [<B>Operation</B>, 'The M365 operation that triggered the match.'],
            ]}
          />
          <P>
            Each row expands for full detail, rendered the same way a real alert would be. This
            audit view is how you validate that suppressions are clearing noise and not hiding
            genuine threats.
          </P>
        </Section>
      </Section>

      {/* ── CONFIGURATION PAGES ─────────────────────────────────────────── */}
      <Section id="config-pages" title="Configuration Pages">
        <P>
          The sidebar under <B>Alerts</B> lists 11 configuration sub-pages. These define the
          allowlists, trusted entities and watchlists that feed the suppression engine. Three are
          Global-Admin-only and simply won't appear for other users. They share common patterns:
        </P>
        <UL>
          <li><B>Add form</B> at the top — fill fields and submit to create an entry.</li>
          <li><B>Filterable, sortable table</B> below — paginated (10/20/50/100 rows).</li>
          <li>
            <B>Edit / Disable</B> actions — identity fields (tenant, target) are immutable; delete
            and re-add to change them. Removal is a <em>soft</em> disable, so entries can be
            re-enabled and history is preserved.
          </li>
          <li><B>"Show disabled"</B> checkbox — reveals soft-deleted entries.</li>
          <li><B>"Reconcile sentinel"</B> button — see the note below.</li>
        </UL>

        <Callout tone="warn">
          <p>
            <B>"Reconcile sentinel" is not related to Microsoft Sentinel.</B> Here, "sentinel" means
            an internal version counter each allowlist keeps. The detection pipeline polls that
            counter every few seconds and reloads the list when it changes — that's how an allowlist
            edit takes effect within seconds rather than at the next full refresh.
          </p>
          <p>
            Every normal add, edit or disable bumps the counter for you. The button is a{' '}
            <B>recovery action</B> for the rare case where the bump didn't land and your change
            doesn't appear to be taking effect. It is safe, non-destructive and rarely needed. Some
            pages also show a sentinel-health banner when a bump is pending retry.
          </p>
        </Callout>

        <Section id="config-aitm" title="AiTM Allowlists" level={3}>
          <GuideCard title="AiTM Trusted Domains">
            <p><B>Scope:</B> Global (all tenants)</p>
            <p>
              Click-host substring allowlist for AiTM detection rules. If a sign-in's click host
              matches one of these domain substrings, the AiTM alert is suppressed. Columns: Domain,
              Notes, Added By, Date, Actions.
            </p>
          </GuideCard>

          <GuideCard title="AiTM Trusted Pairs">
            <p><B>Scope:</B> Per-tenant</p>
            <p>
              Pairs of (click host, sign-in application) that are trusted. Supports wildcards. Used
              when a specific host + app combination is known to be legitimate for a tenant.
            </p>
          </GuideCard>
        </Section>

        <Section id="config-ip" title="IP Lists" level={3}>
          <GuideCard title="Known Malicious IPs">
            <p><B>Scope:</B> Global (cross-tenant threat intelligence)</p>
            <p>
              IPs confirmed as malicious. Alerts from these IPs are <B>never suppressed</B> on rules
              that carry the malicious-IP override. The type dropdown offers <K>c2</K>,{' '}
              <K>phishing</K>, <K>scanner</K>, <K>tor_exit</K>, <K>known_proxy</K>,{' '}
              <K>malware_drop</K>, <K>exfil</K> and <K>unknown</K>, plus a free-text "Other".
              (<K>attacker</K> is applied automatically when IPs are promoted in bulk from an
              investigation case.)
            </p>
            <p>
              Adding an IP that's already listed <B>overwrites</B> its type, source and notes while
              keeping the original date it was first added.
            </p>
            <p>
              Has a <B>"Search cross-tenant activity"</B> button to find all activity from a
              malicious IP across all monitored tenants.
            </p>
          </GuideCard>

          <GuideCard title="Spray-Origin IP Watchlist">
            <p><B>Scope:</B> Global (auto-populated by the pipeline)</p>
            <p>
              Captures attacker IP ranges seen in password-spray attacks against monitored tenants;
              a <em>successful</em> sign-in from a listed range raises a high-severity alert. Entries
              cannot be added by hand — the pipeline writes them and ages them out on its own. Global
              Admins can <B>Revoke</B> (disable) an entry that's causing false positives.
            </p>
            <p>
              Columns: Prefix, IP version, Tenants sprayed, Sprays, First seen, Last seen, Risk,
              Enabled. The number of <em>distinct tenants sprayed</em> is the strongest signal that a
              range is genuinely hostile rather than a shared egress.
            </p>
            <p>
              Non-admins see a read-only view scoped to their own tenants, with the sampled target
              usernames hidden.
            </p>
          </GuideCard>

          <GuideCard title="Defender Pass-through IP Allowlist" badge={<GA />}>
            <p><B>Scope:</B> Global (all tenants)</p>
            <p>
              Benign IPs that Microsoft mis-flags as malicious, causing false positives in Defender /
              Entra pass-through alerts. Entries include a <B>Source service</B> field to document
              why the IP is trusted.
            </p>
            <p>
              Exact IPs only — no CIDR ranges. A known-malicious IP still alerts. This is also where
              the teal <Xref to="inline-suppress">benign-IP quick-suppress</Xref> writes its entries.
            </p>
          </GuideCard>
        </Section>

        <Section id="config-mailbox" title="Mailbox & Account Allowlists" level={3}>
          <GuideCard title="Mailbox-Pair Allowlist">
            <p><B>Scope:</B> Per-tenant, per-rule</p>
            <p>
              Pairs of (Mailbox, Actor) for the mailbox-access rules. Tabs separate entries by
              detection rule, so an entry only affects the rule you added it under. Actor types: UPN,
              App ID, App Name, Service SID, or Any (<K>*</K>).
            </p>
          </GuideCard>

          <GuideCard title="Account Allowlist">
            <p><B>Scope:</B> Per-tenant, per-rule</p>
            <p>
              Service and appliance accounts — Teams-phone station accounts being the main case —
              that generate expected alerts. Works together with Teams-Phone Models below: for that
              rule, an alert is suppressed only when the account is listed <em>and</em> a matching
              phone device enrolled at the same moment.
            </p>
          </GuideCard>

          <GuideCard title="Teams-Phone Models" badge={<GA />}>
            <p><B>Scope:</B> Global (all tenants)</p>
            <p>
              Device-name substring patterns for Teams phone hardware (e.g. <K>poly ccx</K>,{' '}
              <K>yealink mp</K>, <K>audiocodes</K>). Matching is on a substring of the device name,
              so a short pattern covers a whole product family.
            </p>
          </GuideCard>
        </Section>

        <Section id="config-app" title="Application & Group Pair Allowlists" level={3}>
          <GuideCard title="User/App Pair Allowlist">
            <p><B>Scope:</B> Per-tenant, per-rule (or cross-tenant with a wildcard)</p>
            <p>
              Pairs of (User, Application). Tabs separate entries by detection rule. User modes:{' '}
              <em>Full UPN</em>, <em>Any guest from a domain</em> (<K>*@domain.tld</K>, which matches
              external/B2B guests only and cannot suppress internal users), or <em>Any</em> (
              <K>*</K>). App modes: pick a known app, enter a GUID, or <em>Any</em>.
            </p>
            <p>
              The application is matched on its <B>GUID</B>, not its display name — the name shown is
              informational. Wildcarding both user and app at once is rejected.
            </p>
          </GuideCard>

          <GuideCard title="Group/App Pair Allowlist">
            <p><B>Scope:</B> Per-tenant, per-rule (or cross-tenant with a wildcard)</p>
            <p>
              Pairs of (Entra group, Application) — the same idea as User/App, but keyed on group
              membership, which is the better fit when the exception belongs to a role rather than a
              person. The group is matched by <B>display name</B> (pick it from the tenant's groups);
              the app by GUID or <em>Any</em>. There is no "any group" wildcard.
            </p>
            <Callout tone="warn">
              <p>
                <B>Membership is refreshed once daily.</B> A user added to the group today may keep
                generating alerts until the next refresh — that's expected, not a broken allowlist.
              </p>
            </Callout>
          </GuideCard>
        </Section>

        <Section id="config-defender" title="Defender Allowlists" level={3}>
          <GuideCard title="Defender File-Hash Allowlist" badge={<GA />}>
            <p><B>Scope:</B> Global (all tenants)</p>
            <p>
              Pairs of (file hash, threat family) for Defender endpoint alerts. Supports SHA-256,
              SHA-1 and MD5. Includes File name and Signer metadata to document the allowlisted
              binary.
            </p>
            <p>
              The threat family can be pinned to a specific threat or set to <K>*</K> for any threat.
              Pinning it is safer: the same binary flagged under a <em>different</em> threat name will
              still alert. A known-malicious IP still alerts regardless.
            </p>
          </GuideCard>
        </Section>
      </Section>

      {/* ── ALERT RULES ─────────────────────────────────────────────────── */}
      <Section id="alert-rules" title="Alert Rules (Admin)">
        <P>
          The{' '}
          <Link to="/admin/alert-rules" className="text-blue-400 hover:text-blue-300 hover:underline">
            Alert Rules
          </Link>{' '}
          page is Global-Admin only. It lists every detection rule with its type, version, mode and
          enabled state.
        </P>

        <GuideTable
          cols={['Column', 'Description']}
          rows={[
            [
              <B>Rule Name</B>,
              "Display name of the detection rule. Hover to reveal a copy button for the rule's internal ID. Scope badges appear beneath the name when the rule is limited to, or excludes, particular MSPs or tenants.",
            ],
            [
              <B>Type</B>,
              <>
                Python Fn, Config Fn, Log Type, IP Flag, Baseline Deviation or Operation Baseline
                Deviation — see <Xref to="detection-pipeline">Detection Pipeline</Xref>.
              </>,
            ],
            [<B>Version</B>, 'Rule version number, incremented on each save.'],
            [<B>Mode</B>, 'PROD or TEST. Determines available actions.'],
            [<B>Enabled</B>, 'Whether the rule is active in the pipeline. A clock icon indicates the rule also runs on a recurring schedule.'],
            [<B>Actions</B>, 'Edit, Clone, and (TEST only) Delete, Test run, View test results.'],
          ]}
        />

        <Section id="rule-editor" title="Rule Editor" level={3}>
          <P>Clicking <B>Edit</B> on a rule opens the editor panel:</P>

          <GuideTable
            cols={['Field', 'Description']}
            rows={[
              [<B>Rule Name</B>, 'Display name shown in alerts.'],
              [<B>Type</B>, <>The rule type — see <Xref to="detection-pipeline">Detection Pipeline</Xref>.</>],
              [<B>Python Function</B>, 'Name of the Python function in the pipeline codebase (for function-backed rules).'],
              [<B>Version</B>, 'Auto-incremented version number.'],
              [<B>Enabled</B>, 'Toggle to enable or disable the rule.'],
              [
                <B>Severity</B>,
                'Critical, High, Medium, Low or Informational. Drives the alert badge, the email digest and — where per-tenant ticket routing is configured — the PSA ticket priority.',
              ],
              [<B>Cooldown (minutes)</B>, 'Deduplication window — prevents repeat alerts for the same subject within this period.'],
              [<B>Description</B>, 'Free-text description of what the rule detects.'],
              [
                <B>Parameters (JSON)</B>,
                "Rule-specific configuration passed to the detection function. Edited as raw JSON; keys the UI doesn't surface are preserved on save.",
              ],
              [
                <B>Suppressed Event Countries</B>,
                "On new-country rules: suppress alerts when the event's country is in this list, regardless of baseline state.",
              ],
              [
                <B>Alert on every matching event</B>,
                'Toggle for burst-style rules (e.g. Defender pass-through) that should alert on each match rather than collapsing them.',
              ],
              [
                <B>Scheduled Testing</B>,
                'TEST-mode only — runs the rule against recent data on a schedule so you can watch its behaviour before promoting it.',
              ],
              [
                <B>Excluded Alert Titles / Descriptions / Actors</B>,
                <>
                  On Microsoft-security pass-through rules: managed lists of the entries created by
                  the <Xref to="inline-suppress">rose, orange and violet quick-suppress actions</Xref>.
                  Review and remove them here. They affect noise suppression only, never detection
                  logic.
                </>,
              ],
            ]}
          />

          <H4>Rule Scope</H4>
          <P>Each rule can be scoped to include or exclude MSPs and tenants, plus exclude IPs by provider:</P>
          <UL>
            <li>
              <B>Include MSPs</B> — only run this rule for these MSPs (empty = all). New tenants
              joining an included MSP inherit the rule automatically.
            </li>
            <li><B>Exclude MSPs</B> — skip these MSPs.</li>
            <li><B>Include Tenants</B> — only run for these tenants.</li>
            <li><B>Exclude Tenants</B> — skip these tenants.</li>
            <li><B>Exclude IPs by Provider</B> — ignore events whose IP resolves to a listed ISP/ASN.</li>
          </UL>
          <P>Include narrows first, then Exclude removes.</P>

          <Callout tone="warn">
            <p>
              <B>MSP include is fail-closed.</B> An MSP code that resolves to no tenants matches{' '}
              <em>nothing</em> — it does not silently fall back to "all tenants". The portal
              validates codes on save to catch typos, but the behaviour is worth knowing: an
              over-narrow include list is a silent detection gap.
            </p>
          </Callout>

          <H4>Suppression Toggles</H4>
          <P>The Suppression block holds 16 toggles, but they aren't all the same kind of thing:</P>
          <UL>
            <li>
              <B>14 suppression tiers</B> — device posture (Entra joined, Intune managed, Intune
              compliant, DRMM match), baselines (MSP tenant, user, provider-in-user-baseline), and
              the allowlist tiers (mailbox pair, user/app pair, group/app pair, file hash, benign IP,
              Teams-phone, CTAP trust). Each turns on a specific check; several map into the same{' '}
              <Xref to="suppress-methods">family</Xref>.
            </li>
            <li>
              <B>1 modifier</B> — <em>IPv6 /64 prefix match</em>, which widens IPv6 comparison to the
              /64 prefix on the IP-based tiers. It suppresses nothing on its own and has no effect on
              IPv4.
            </li>
            <li>
              <B>1 override</B> — <em>Alert if a Known Malicious IP</em>, which works in the opposite
              direction: it <B>forces</B> alerts through when the source IP is on the Known Malicious
              IPs list, regardless of what the suppression tiers say.
            </li>
          </UL>
          <P>
            Turning a suppression tier off means that one check no longer runs for this rule — every
            other enabled tier still applies. Turning one <em>on</em> only has an effect if the
            corresponding allowlist has entries; that's also what makes the matching{' '}
            <Xref to="inline-suppress">quick-suppress bell</Xref> appear on the rule's alerts.
          </P>
        </Section>

        <Section id="prod-test" title="PROD / TEST Workflow" level={3}>
          <GuideTable
            cols={['Mode', 'Available actions', 'Behaviour']}
            rows={[
              [
                <B>PROD</B>,
                'Edit, Clone, Demote to TEST',
                <>
                  Alerts are created in the live pipeline. Cannot be deleted or test-run —{' '}
                  <B>Demote to TEST</B> first.
                </>,
              ],
              [
                <B>TEST</B>,
                'Edit, Clone, Delete, Test run, View test results, Promote to PROD',
                <>
                  Matches are recorded with "Test" status, visible in the alerts list only with{' '}
                  <B>Show Test Alerts</B> ticked. They do not raise tickets, emails or webhooks. Run
                  ad-hoc tests over a historical date range and review the results before promoting.
                </>,
              ],
            ]}
          />

          <Callout tone="info">
            <p>
              <B>Every field is editable in both modes.</B> Name, severity, parameters, scope and
              suppression toggles can all be changed in place on a PROD rule. Mode controls only
              three things: whether the rule can be <em>deleted</em>, whether it can be{' '}
              <em>test-run</em>, and whether <em>Scheduled Testing</em> is available.
            </p>
          </Callout>

          <Callout tone="warn">
            <p>
              Because PROD rules are directly editable, a change to a live rule takes effect on the
              next pipeline cycle for every in-scope tenant — there is no staging step. Edit a PROD
              rule deliberately, and prefer cloning to TEST when you want to experiment. Promoting a
              TEST rule to PROD has the same reach: always validate its test results first.
            </p>
          </Callout>

          <P>
            A test run clears the rule's previous test results before it starts, so what you see
            under the run header is only that run's matches.
          </P>
        </Section>
      </Section>
    </GuideLayout>
  );
}
